import { CLINIC_INFO } from '../data';
import { Calendar, Phone, Mail, HelpCircle, ChevronRight, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function Booking() {
  useEffect(() => {
    document.title = "CDH Sports- og Wellness Massage - Bestil tid";
  }, []);

  return (
    <div className="bg-white min-h-screen flex flex-col">
      {/* Hero Section */}
      <section className="relative h-[70vh] md:h-[75vh] lg:h-[80vh] flex items-center overflow-hidden pt-32 md:pt-40 lg:pt-48">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/767bb60f-8ec8-4a4c-940e-ed49b89257a3/125d8ef7-2189-4bb7-a52a-ada72dea3de8.jpg"
            alt="Bestil tid hos CDH Massage"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-blue-900/60 z-10" />
        </div>

        <div className="container mx-auto px-4 relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-[1.1]">
              Bestil tid online
            </h1>
            <p className="text-sm md:text-base text-white/90 mb-10 leading-relaxed font-light max-w-xl">
              Vælg din behandling og find en tid der passer dig i mit bookingsystem.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 flex-grow bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-blue-100">
              <div className="grid grid-cols-1 lg:grid-cols-5">
                {/* Left: Info */}
                <div className="lg:col-span-2 bg-blue-50 p-12 space-y-10 border-r border-blue-100">
                  <div>
                    <h3 className="text-2xl font-bold text-blue-900 mb-6">Før du booker</h3>
                    <p className="text-gray-600 mb-8 leading-relaxed">
                      Er du i tvivl om hvilken behandling der passer til dig, kan du læse mere under behandlinger eller kontakte klinikken.
                    </p>
                    <Link
                      to="/behandlinger"
                      className="inline-flex items-center text-blue-700 font-bold hover:text-blue-900 group"
                    >
                      Se alle behandlinger
                      <ChevronRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>

                  <div className="space-y-6">
                    <div className="flex items-start">
                      <CheckCircle2 className="w-6 h-6 text-emerald-500 mr-4 shrink-0" />
                      <p className="text-sm text-gray-700 font-medium">Hurtig online booking</p>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle2 className="w-6 h-6 text-emerald-500 mr-4 shrink-0" />
                      <p className="text-sm text-gray-700 font-medium">Bekræftelse med det samme</p>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle2 className="w-6 h-6 text-emerald-500 mr-4 shrink-0" />
                      <p className="text-sm text-gray-700 font-medium">Flyt eller afbestil op til 24t før</p>
                    </div>
                  </div>

                  <div className="pt-10 border-t border-blue-100">
                    <h4 className="font-bold text-blue-900 mb-4 flex items-center">
                      <HelpCircle className="w-5 h-5 mr-2 text-blue-500" />
                      Brug for hjælp?
                    </h4>
                    <div className="space-y-4">
                      <a href={`tel:${CLINIC_INFO.phone}`} className="flex items-center text-gray-600 hover:text-blue-600 transition-colors">
                        <Phone className="w-4 h-4 mr-3" />
                        <span className="font-medium">{CLINIC_INFO.phone}</span>
                      </a>
                      <a href={`mailto:${CLINIC_INFO.email}`} className="flex items-center text-gray-600 hover:text-blue-600 transition-colors">
                        <Mail className="w-4 h-4 mr-3" />
                        <span className="font-medium">{CLINIC_INFO.email}</span>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Right: CTA to system */}
                <div className="lg:col-span-3 p-12 md:p-20 flex flex-col justify-center items-center text-center">
                  <div className="w-24 h-24 bg-blue-100 rounded-3xl flex items-center justify-center mb-10 rotate-3">
                    <Calendar className="w-12 h-12 text-blue-600 -rotate-3" />
                  </div>
                  <h2 className="text-3xl font-bold text-blue-900 mb-6">Åbn bookingsystem</h2>
                  <p className="text-gray-600 mb-12 text-lg">
                    Jeg benytter et eksternt bookingsystem for at give dig den bedste oplevelse. Tryk på knappen herunder for at se ledige tider.
                  </p>
                  
                  <a
                    href="https://cdhmassage.onlinebooq.dk/" // Example URL from standard Onlinebooq setups often used in DK
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full max-w-sm bg-blue-600 hover:bg-blue-700 text-white py-4 lg:py-5 rounded-2xl font-bold text-lg lg:text-xl shadow-xl transition-all flex items-center justify-center hover:scale-[1.02] active:scale-95"
                  >
                    Bestil tid nu
                    <ChevronRight className="w-6 h-6 ml-2" />
                  </a>
                  
                  <p className="mt-8 text-sm text-gray-400">
                    Siden åbner i et nyt vindue
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
